import React, { useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { Bug, Lightbulb, MessageCircle, CheckCircle2, Star } from 'lucide-react';
import api from '../services/api';
import type { SubmitFeedbackPayload, FeedbackType } from '../types/feedback.types';

interface FeedbackFormProps {
    onSuccess?: () => void;
}

const FeedbackForm: React.FC<FeedbackFormProps> = ({ onSuccess }) => {
    const queryClient = useQueryClient();
    const [type, setType] = useState<FeedbackType>('general');
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [rating, setRating] = useState<number>(0);
    const [hoveredStar, setHoveredStar] = useState<number>(0);
    const [showSuccess, setShowSuccess] = useState(false);
    const [error, setError] = useState('');

    const mutation = useMutation(
        (data: SubmitFeedbackPayload) =>
            api.feedback.submitFeedback(data),
        {
            onSuccess: () => {
                queryClient.invalidateQueries(['my-feedback']);
                queryClient.invalidateQueries(['feedback-stats']);
                queryClient.invalidateQueries(['admin-feedback']);
                setShowSuccess(true);
                setTitle('');
                setDescription('');
                setRating(0);
                setType('general');
                setError('');
                setTimeout(() => {
                    setShowSuccess(false);
                    onSuccess?.();
                }, 2000);
            },
            onError: (err: unknown) => {
                const message = (err as any)?.response?.data?.error || 'Failed to submit feedback. Please try again.';
                setError(message);
            },
        }
    );

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        if (!title.trim()) {
            setError('Title is required');
            return;
        }
        if (!description.trim()) {
            setError('Description is required');
            return;
        }

        mutation.mutate({
            type,
            title: title.trim(),
            description: description.trim(),
            page_url: window.location.href,
            browser_info: navigator.userAgent,
            ...(rating > 0 ? { rating } : {}),
        });
    };

    const descriptionPlaceholders: Record<FeedbackType, string> = {
        bug: 'What happened? What did you expect? Steps to reproduce...',
        feature: 'Describe the feature you\'d like to see...',
        general: 'Share your thoughts...',
    };

    if (showSuccess) {
        return (
            <div className="feedback-success">
                <CheckCircle2 size={48} color="#059669" />
                <h3>Thank you!</h3>
                <p>Your feedback has been submitted successfully.</p>
            </div>
        );
    }

    return (
        <form className="feedback-form" onSubmit={handleSubmit}>
            {error && <div className="feedback-error">{error}</div>}

            <div className="feedback-form-group">
                <span id="feedback-type-label" className="feedback-form-label">Type</span>
                <div className="feedback-type-selector" role="group" aria-labelledby="feedback-type-label">
                    <button
                        type="button"
                        className={`feedback-type-btn ${type === 'bug' ? 'active' : ''}`}
                        onClick={() => setType('bug')}
                    >
                        <Bug size={18} />
                        Bug Report
                    </button>
                    <button
                        type="button"
                        className={`feedback-type-btn ${type === 'feature' ? 'active' : ''}`}
                        onClick={() => setType('feature')}
                    >
                        <Lightbulb size={18} />
                        Feature
                    </button>
                    <button
                        type="button"
                        className={`feedback-type-btn ${type === 'general' ? 'active' : ''}`}
                        onClick={() => setType('general')}
                    >
                        <MessageCircle size={18} />
                        General
                    </button>
                </div>
            </div>

            <div className="feedback-form-group">
                <label htmlFor="feedback-title">Title *</label>
                <input
                    id="feedback-title"
                    className="feedback-input"
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="Brief summary of your feedback"
                    maxLength={200}
                />
            </div>

            <div className="feedback-form-group">
                <label htmlFor="feedback-description">Description *</label>
                <textarea
                    id="feedback-description"
                    className="feedback-textarea"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder={descriptionPlaceholders[type]}
                />
            </div>

            <div className="feedback-form-group">
                <span id="feedback-rating-label" className="feedback-form-label">Rating (optional)</span>
                <div className="feedback-rating" role="group" aria-labelledby="feedback-rating-label">
                    {[1, 2, 3, 4, 5].map((star) => (
                        <button
                            key={star}
                            type="button"
                            className={`feedback-star ${star <= (hoveredStar || rating) ? 'active' : ''}`}
                            onClick={() => setRating(star === rating ? 0 : star)}
                            onMouseEnter={() => setHoveredStar(star)}
                            onMouseLeave={() => setHoveredStar(0)}
                            aria-label={`${star} star${star > 1 ? 's' : ''}`}
                        >
                            <Star size={24} fill={star <= (hoveredStar || rating) ? '#F59E0B' : 'none'} />
                        </button>
                    ))}
                </div>
            </div>

            <button
                type="submit"
                className="feedback-submit-btn"
                disabled={mutation.isLoading}
            >
                {mutation.isLoading ? 'Submitting...' : 'Submit Feedback'}
            </button>
        </form>
    );
};

export default FeedbackForm;
